import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFestivalDetailQuery } from '@/queries/useFestivalDetailQuery';
import { useFaqsQuery } from '@/queries/faq';
import { useFestivalVendorsQuery } from '@/queries/vendors';
import { useSponsorsQuery } from '@/queries/sponsors';
import { useServicesQuery } from '@/queries/services';
import { usePerformancesQuery } from '@/queries/stages';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import { Loading, LocationMapField } from '../controls';

function InfoBlock({ icon, title, children }) {
  if (!children) {
    return null;
  }
  return (
    <section className="bethere-info-block">
      <h2 className="flex items-center gap-2 text-base font-bold text-[#121212]">
        <FontAwesomeIcon
          icon={icon}
          className="text-[var(--bethere-primary)]"
        />
        {title}
      </h2>
      {children}
    </section>
  );
}

function FaqAccordion({ faqs }) {
  const [openId, setOpenId] = useState(null);
  if (!faqs?.length) {
    return null;
  }
  return (
    <div className="faq-accordion">
      {faqs.map((faq) => {
        const active = openId === faq.faqId;
        return (
          <div
            key={faq.faqId}
            className={active ? 'faq-item active' : 'faq-item'}
          >
            <button
              type="button"
              className="faq-question"
              onClick={() => setOpenId(active ? null : faq.faqId)}
            >
              {faq.faqName}
              <FontAwesomeIcon
                icon={['fas', 'chevron-down']}
                className={active ? 'rotate-180' : ''}
              />
            </button>
            <div className="faq-answer">
              <p className="m-0 px-4 pb-4 pt-0 text-[var(--bethere-f-p)] leading-relaxed">
                {faq.faqDescription}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatHref(url) {
  if (!url?.trim()) {
    return null;
  }
  const v = url.trim();
  return v.includes('://') ? v : `https://${v}`;
}

function formatDateRange(festival) {
  const dates = festival?.dates?.length
    ? festival.dates
    : festival?.startDate
      ? [festival.startDate]
      : [];
  if (!dates.length) {
    return null;
  }
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (!first?.startTime) {
    return null;
  }
  const start = moment(first.startTime);
  const end = last?.endTime ? moment(last.endTime) : null;
  const sameDay = end && start.isSame(end, 'day');
  if (sameDay && end) {
    return `${start.format('MMM D, YYYY')} · ${start.format('h:mm A')} — ${end.format('h:mm A')}`;
  }
  if (end) {
    return `${start.format('MMM D, h:mm A')} — ${end.format('MMM D, h:mm A')}`;
  }
  return start.format('MMM D, YYYY · h:mm A');
}

function buildScheduleRows(festival, performances) {
  if (performances?.length) {
    return performances
      .filter((p) => p.startTime)
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .map((p) => ({
        key: p.performanceId ?? `${p.startTime}-${p.performer?.performerId}`,
        time: moment(p.startTime).format('h:mm A'),
        activity:
          p.performer?.performerName || p.performanceName || 'Performance',
        location: p.stage?.stageName || festival.location?.locationName || '—',
      }));
  }
  const dates = festival?.dates?.length
    ? festival.dates
    : festival?.startDate
      ? [festival.startDate]
      : [];
  return dates
    .filter((d) => d.startTime)
    .map((d, i) => ({
      key: d.startTimeMS ?? i,
      time: moment(d.startTime).format('h:mm A'),
      activity: 'Festival hours',
      location: festival.location?.locationName || '—',
    }));
}

export function FestivalView(props) {
  const festivalId = props.match?.params?.id;
  const setFestival = useFestivalWorkspaceStore((s) => s.setFestival);

  const detailQ = useFestivalDetailQuery(festivalId);
  const faqsQ = useFaqsQuery(festivalId);
  const vendorsQ = useFestivalVendorsQuery(festivalId);
  const sponsorsQ = useSponsorsQuery(festivalId);
  const servicesQ = useServicesQuery(festivalId);
  const performancesQ = usePerformancesQuery(festivalId);

  const festival = detailQ.data;

  useEffect(() => {
    if (detailQ.isSuccess && detailQ.data) {
      setFestival(detailQ.data);
    }
  }, [detailQ.isSuccess, detailQ.data, setFestival]);

  if (detailQ.isPending) {
    return <Loading />;
  }

  if (detailQ.isError || !festival) {
    return (
      <div className="py-16 text-center text-red-600">
        Could not load this event.{' '}
        <Link to="/" className="font-bold text-[var(--bethere-primary)]">
          Back to events
        </Link>
      </div>
    );
  }

  const faqs = faqsQ.data ?? [];
  const vendors = (vendorsQ.data ?? []).map((fv) => fv.vendor).filter(Boolean);
  const sponsors = sponsorsQ.data ?? [];
  const services = servicesQ.data ?? [];
  const performances = performancesQ.data ?? [];
  const scheduleRows = buildScheduleRows(festival, performances);
  const dateRange = formatDateRange(festival);
  const links = festival.links ?? {};
  const webHref = formatHref(links.web);

  return (
    <article className="bethere-festival-view">
      <nav className="breadcrumb-container" aria-label="Breadcrumb">
        <ul className="breadcrumb m-0 list-none p-0">
          <li>
            <Link to="/">Event Management</Link>
          </li>
          <li aria-current="page">{festival.name}</li>
        </ul>
        <Link
          to={`/festival/${festival.festivalId}`}
          className="bethere-view-edit-link"
        >
          <FontAwesomeIcon icon={['fas', 'pen-to-square']} /> Edit event
        </Link>
      </nav>

      <div className="detail-hero">
        {festival.photoURL ? (
          <img
            src={festival.photoURL}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-neutral-700 text-white/70">
            No image
          </div>
        )}
      </div>

      <div className="bethere-content-container">
        <div className="event-main-info min-w-0">
          <div className="glass-panel p-6 sm:p-8">
            <h1 className="mb-6 text-[length:var(--bethere-f-h1)] font-bold leading-[1.2] text-[#121212]">
              {festival.name}
            </h1>

            {festival.description && (
              <InfoBlock
                icon={['fas', 'circle-info']}
                title="General Information"
              >
                <p className="m-0 leading-relaxed text-[var(--bethere-f-p)] text-[#121212]">
                  {festival.description}
                </p>
              </InfoBlock>
            )}

            {scheduleRows.length > 0 && (
              <InfoBlock icon={['fas', 'calendar-days']} title="Event Schedule">
                <div className="table-responsive">
                  <table className="schedule-table">
                    <thead>
                      <tr>
                        <th scope="col">Time</th>
                        <th scope="col">Activity</th>
                        <th scope="col">Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scheduleRows.map((row) => (
                        <tr key={row.key}>
                          <th scope="row">{row.time}</th>
                          <td>{row.activity}</td>
                          <td>{row.location}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </InfoBlock>
            )}

            {services.length > 0 && (
              <InfoBlock
                icon={['fas', 'hand-holding-heart']}
                title="Available Services"
              >
                <ul className="bethere-check-list">
                  {services.map((svc) => (
                    <li key={svc.serviceId}>
                      <FontAwesomeIcon icon={['fas', 'circle-check']} />
                      <span>
                        <strong>{svc.serviceName}</strong>
                        {svc.serviceDescription && (
                          <span className="text-[var(--muted-foreground)]">
                            {' '}
                            — {svc.serviceDescription}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </InfoBlock>
            )}

            {faqs.length > 0 && (
              <InfoBlock
                icon={['fas', 'circle-question']}
                title="Frequently Asked Questions"
              >
                <FaqAccordion faqs={faqs} />
              </InfoBlock>
            )}

            {vendors.length > 0 && (
              <InfoBlock icon={['fas', 'shop']} title="Event Vendors">
                <div className="event-vendors">
                  {vendors.map((vendor) => {
                    const href = formatHref(vendor.links?.web);
                    const inner = (
                      <>
                        {vendor.photoURL ? (
                          <img src={vendor.photoURL} alt="" />
                        ) : (
                          <span
                            className="vendor-detail-placeholder"
                            aria-hidden
                          />
                        )}
                        <p className="small m-0 text-sm">
                          <strong>{vendor.vendorName}</strong>
                        </p>
                      </>
                    );
                    return href ? (
                      <a
                        key={vendor.vendorId}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="vendor-detail"
                      >
                        {inner}
                      </a>
                    ) : (
                      <div key={vendor.vendorId} className="vendor-detail">
                        {inner}
                      </div>
                    );
                  })}
                </div>
              </InfoBlock>
            )}
          </div>
        </div>

        <aside className="bethere-detail-sidebar">
          <div className="glass-panel p-6">
            <ul className="meta-list m-0 list-none p-0">
              {dateRange && (
                <li>
                  <FontAwesomeIcon icon={['fas', 'calendar-days']} />
                  <div>
                    <strong>Date &amp; Time</strong>
                    {dateRange}
                  </div>
                </li>
              )}
              {(webHref ||
                links.facebook ||
                links.twitter ||
                links.instagram) && (
                <li className="event-socials">
                  <FontAwesomeIcon icon={['fas', 'link']} />
                  <div>
                    <strong>Organizer Website</strong>
                    {webHref && (
                      <a
                        href={webHref}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {links.web}
                      </a>
                    )}
                    <div className="event-socials-icons">
                      {links.facebook && (
                        <a
                          href={formatHref(links.facebook)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Facebook"
                        >
                          <FontAwesomeIcon icon={['fab', 'facebook']} />
                        </a>
                      )}
                      {links.twitter && (
                        <a
                          href={formatHref(links.twitter)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="X"
                        >
                          <FontAwesomeIcon icon={['fab', 'x-twitter']} />
                        </a>
                      )}
                      {links.instagram && (
                        <a
                          href={formatHref(links.instagram)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Instagram"
                        >
                          <FontAwesomeIcon icon={['fab', 'instagram']} />
                        </a>
                      )}
                    </div>
                  </div>
                </li>
              )}
              {festival.location?.locationName && (
                <li>
                  <FontAwesomeIcon icon={['fas', 'location-dot']} />
                  <div>
                    <strong>Venue Location</strong>
                    {festival.location.locationName}
                  </div>
                </li>
              )}
            </ul>
            {festival.location && (
              <div className="map-box mt-4 overflow-hidden rounded-[var(--bethere-radius-md)]">
                <LocationMapField
                  location={festival.location}
                  readOnly
                  showSearch={false}
                />
              </div>
            )}
          </div>

          {sponsors.length > 0 && (
            <div className="glass-panel p-6">
              <InfoBlock icon={['fas', 'shop']} title="Event Sponsors">
                <div className="event-sponsors">
                  {sponsors.map((sponsor) => {
                    const href = formatHref(sponsor.links?.web);
                    const inner = (
                      <>
                        {sponsor.photoURL ? (
                          <img src={sponsor.photoURL} alt="" />
                        ) : (
                          <span
                            className="sponsor-detail-placeholder"
                            aria-hidden
                          />
                        )}
                        <p className="small m-0 text-sm">
                          <strong>{sponsor.sponsorName}</strong>
                        </p>
                      </>
                    );
                    return href ? (
                      <a
                        key={sponsor.sponsorId}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sponsor-detail"
                      >
                        {inner}
                      </a>
                    ) : (
                      <div key={sponsor.sponsorId} className="sponsor-detail">
                        {inner}
                      </div>
                    );
                  })}
                </div>
              </InfoBlock>
            </div>
          )}
        </aside>
      </div>
    </article>
  );
}
